<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.schools', 2),
                        href: meta.links.index,
                    },
                    {
                        name: school.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.schools', 1) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.name')" for="name" />
                        {{ school.name }}
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_code')" for="school_code" />
                        {{ school.school_code }}
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.languages', 1)" for="language" />
                        {{ school.locale }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <hr class="mt-5" />

        <AppWell class="mt-5">
            <DashboardCentralSchoolWidgets :widgets="widgets" />

            <div class="md:grid md:grid-cols-3 md:gap-6 mt-5">
                <AppCard>
                    <div class="p-4">
                        <ul class="divide-gray-300 divide-y-2">
                            <li
                                class="flex justify-between py-2 px-2"
                                :class="{
                                    'text-gray-400': item.value === 0,
                                    'text-gray-700': item.value > 0,
                                    'bg-gray-50': index % 2 === 0,
                                }"
                                v-for="(item, index) in meta.info.summary"
                                :key="item.text"
                            >
                                <div>
                                    {{ item.text }}
                                </div>
                                <div class="tabular-nums">
                                    {{ item.value }}
                                </div>
                            </li>
                        </ul>
                    </div>
                </AppCard>
                <AppCard>
                    <div class="p-4">
                        <div>
                            {{ $t('messages.import') }}
                        </div>

                        <div>
                            <div class="mt-3" v-for="category in importCategories" :key="category.id">
                                <ConfirmActionButton
                                    :href="category.url"
                                    method="post"
                                    :confirm-text="$t('messages.import')"
                                    :title="`${$t('messages.import')} - ${category.text}`"
                                    :button-text="category.text"
                                    :desired-action="!category.is_imported"
                                    :params="importParams"
                                >
                                    <div>({{ category.stage_name }})</div>
                                    <div class="mt-1 text-gray-600">({{ category.school_origin_name }})</div>
                                    <div class="mt-3">
                                        <label class="text-xs">
                                            <input
                                                type="checkbox"
                                                name="show_questions_count"
                                                id=""
                                                v-model="importParams.questions"
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <span>{{ $tc('messages.questions', 2) }}</span>
                                        </label>
                                    </div>
                                </ConfirmActionButton>
                            </div>
                        </div>
                    </div>
                </AppCard>
            </div>
        </AppWell>

        <hr class="mt-5" />

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.features', 2) }}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class=""
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="6" cy="10" r="2"></circle>
                    <line x1="6" y1="4" x2="6" y2="8"></line>
                    <line x1="6" y1="12" x2="6" y2="20"></line>
                    <circle cx="12" cy="16" r="2"></circle>
                    <line x1="12" y1="4" x2="12" y2="14"></line>
                    <line x1="12" y1="18" x2="12" y2="20"></line>
                    <circle cx="18" cy="7" r="2"></circle>
                    <line x1="18" y1="4" x2="18" y2="5"></line>
                    <line x1="18" y1="9" x2="18" y2="20"></line>
                </svg>
            </template>
            <div>
                <div v-for="feature in featureToggles" :key="feature.name">
                    <InertiaLink
                        method="patch"
                        as="button"
                        :href="feature.url"
                        preserve-scroll
                        :class="feature.is_active ? 'bg-indigo-600' : 'bg-gray-200'"
                        :only="['featureToggles']"
                        class="relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        role="switch"
                        aria-checked="false"
                    >
                        <span class="sr-only">role</span>
                        <span
                            :class="feature.is_active ? 'translate-x-5' : 'translate-x-0'"
                            aria-hidden="true"
                            class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200"
                        ></span>
                    </InertiaLink>
                    {{ feature.display_name }}
                </div>
            </div>
        </TitleFieldsFormSection>
    </PanelPage>
</template>

<script>
import CentralLayout from '@/Shared/CentralLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import AppWell from '@/Shared/AppWell.vue';
import DashboardCentralSchoolWidgets from '@/components/DashboardCentralSchoolWidgets.vue';
import { Link as InertiaLink } from '@inertiajs/vue3';
import AppCard from '@/Shared/AppCard.vue';
import ConfirmActionButton from '@/components/ConfirmActionButton.vue';

export default {
    components: {
        ConfirmActionButton,
        AppCard,
        PanelPage,
        TitleFieldsFormSection,
        FormLabel,
        AppWell,
        DashboardCentralSchoolWidgets,
        InertiaLink,
    },
    layout: CentralLayout,
    name: 'Show',
    props: ['meta', 'options', 'widgets', 'school', 'importCategories', 'featureToggles'],
    data() {
        return {
            importParams: {
                questions: false,
            },
        };
    },
};
</script>
